import { EntityId, createSelector } from "@reduxjs/toolkit";

import { DateTime } from "luxon";

import { RootState } from "app/store";

import { appFeaturesSelectors } from "features/appFeatures/store/selectors";
import { AppFeature } from "features/appFeatures/types/AppFeature";
import { playbackSelectors } from "./selectors.playback";
import { PersistedRecording } from "types/PersistedRecording";
import { getPersistedRecordingWithPlaybackState } from "./helpers";
import { caseViewerSelectors } from "./selectors";
import { CaseViewerState } from "./state";

const selectShowStatistics = createSelector(
    [
        caseViewerSelectors.selectCaseViewer,
        (state: RootState) =>
            appFeaturesSelectors.selectUserHasFeatures(state, [
                AppFeature.AdminDebugTools,
            ]),
    ],
    (state: CaseViewerState, hasDebugModeModeAccess: boolean) =>
        hasDebugModeModeAccess && state.debugShowStatistics,
);

const selectShowBufferingRecordings = createSelector(
    [
        caseViewerSelectors.selectCaseViewer,
        (state: RootState) =>
            appFeaturesSelectors.selectUserHasFeatures(state, [
                AppFeature.AdminDebugTools,
            ]),
    ],
    (state: CaseViewerState, hasAdminDebugTools: boolean) =>
        hasAdminDebugTools && state.debugShowBufferingRecordings,
);

const selectShowRecordingsOutsidePlaybackAndBufferingWindow = createSelector(
    [
        caseViewerSelectors.selectCaseViewer,
        (state: RootState) =>
            appFeaturesSelectors.selectUserHasFeatures(state, [
                AppFeature.AdminDebugTools,
            ]),
    ],
    (state: CaseViewerState, hasAdminDebugTools: boolean) =>
        hasAdminDebugTools &&
        state.debugShowRecordingsOutsidePlaybackAndBufferingWindow,
);

const selectShowForwardAndReversedRecordings = createSelector(
    [
        caseViewerSelectors.selectCaseViewer,
        (state: RootState) =>
            appFeaturesSelectors.selectUserHasFeatures(state, [
                AppFeature.AdminDebugTools,
            ]),
    ],
    (state: CaseViewerState, hasAdminDebugTools: boolean) =>
        hasAdminDebugTools && state.debugShowForwardAndReversedRecordings,
);

const selectShowVideoDirectionInformation = createSelector(
    [
        caseViewerSelectors.selectCaseViewer,
        (state: RootState) =>
            appFeaturesSelectors.selectUserHasFeatures(state, [
                AppFeature.AdminDebugTools,
            ]),
    ],
    (state: CaseViewerState, hasAdminDebugTools: boolean) =>
        hasAdminDebugTools && state.debugShowVideoDirectionInformation,
);

const selectShowSystemControls = createSelector(
    [
        caseViewerSelectors.selectCaseViewer,
        (state: RootState) =>
            appFeaturesSelectors.selectUserHasFeatures(state, [
                AppFeature.AdminDebugTools,
            ]),
    ],
    (state: CaseViewerState, hasAdminDebugTools: boolean) =>
        hasAdminDebugTools && state.debugShowSystemControls,
);

const selectPersistedRecordingsWithPlaybackStateByDossierDeviceIdFactory = () =>
    createSelector(
        [
            (state: RootState, dossierDeviceId: EntityId) => dossierDeviceId,
            playbackSelectors.selectAllPersistedRecordings,
            playbackSelectors.selectCurrentPlaybackTimeStamp,
            playbackSelectors.selectBufferDateTimeRange,
        ],
        (
            dossierDeviceId: EntityId,
            persistedRecordings: PersistedRecording[],
            currentPlaybackTimeStamp: number,
            bufferWindow: { startDateTime: string; endDateTime: string },
        ) => {
            const devicePersistedRecordings = persistedRecordings.filter(
                (x) => x.dossierDeviceId === dossierDeviceId,
            );

            const bufferStartDateTimeTicks = DateTime.fromISO(
                bufferWindow.startDateTime,
            ).valueOf();
            const bufferEndDateTimeTicks = DateTime.fromISO(
                bufferWindow.endDateTime,
            ).valueOf();

            return devicePersistedRecordings.map((recording) =>
                getPersistedRecordingWithPlaybackState(
                    bufferStartDateTimeTicks,
                    bufferEndDateTimeTicks,
                    currentPlaybackTimeStamp,
                    recording,
                ),
            );
        },
    );

export const playbackDebugSelectors = {
    selectShowStatistics,
    selectShowBufferingRecordings,
    selectShowRecordingsOutsidePlaybackAndBufferingWindow,
    selectShowForwardAndReversedRecordings,
    selectShowVideoDirectionInformation,
    selectShowSystemControls,
    selectPersistedRecordingsWithPlaybackStateByDossierDeviceIdFactory,
};
